import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { useOTBookings } from './hooks/useOTBookings';
import { handleExportPDF } from './utils/otBookingUtils';
import BookingFilters from './components/BookingFilters';
import BookingTable from './components/BookingTable';
import BookingMobileCards from './components/BookingMobileCards';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const OTBookingList = () => {
  const navigate = useNavigate();
  const {
    otBookings,
    filteredRows,
    setFilteredRows,
    loading,
    error,
    setError,
    successMessage,
    setSuccessMessage,
    deleteBooking,
  } = useOTBookings();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'booking_datetime', direction: 'desc' });

  // Filter logic
  useEffect(() => {
    let filtered = otBookings;

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(booking =>
        (booking.patient_name || '').toString().toLowerCase().includes(lowerSearch) ||
        (booking.mobile || '').toString().includes(searchTerm) ||
        (booking.uhid_no || '').toString().toLowerCase().includes(lowerSearch) ||
        (booking.email || '').toString().toLowerCase().includes(lowerSearch) ||
        (booking.type_of_surgery || '').toString().toLowerCase().includes(lowerSearch) ||
        (booking.surgical_code || '').toString().toLowerCase().includes(lowerSearch) ||
        (booking.priority_status || '').toString().toLowerCase().includes(lowerSearch) ||
        (booking.room_no || '').toString().toLowerCase().includes(lowerSearch)
      );
    }

    if (startDate && !endDate) {
      setEndDate(new Date().toISOString().split('T')[0]);
    }

    if (startDate && endDate) {
      filtered = filtered.filter(booking => {
        const bookingDate = new Date(booking.booking_datetime);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return bookingDate >= start && bookingDate <= end;
      });
    }

    // Apply Sorting
    filtered.sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredRows(filtered);
  }, [searchTerm, startDate, endDate, otBookings, setFilteredRows, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const validateDateRange = (start, end) => {
    if (start && end) {
      const s = new Date(start);
      const e = new Date(end);
      if (s > e) return 'Start date cannot be later than end date';
    }
    return '';
  };

  const handleDateFilterAction = () => {
    const err = validateDateRange(startDate, endDate);
    if (err) {
      setDateError(err);
      return;
    }
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return;
    }
    setError('');
    setSuccessMessage(`Filtered ${filteredRows.length} records for date range`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchTerm('');
    setDateError('');
    setError('');
    setSuccessMessage('');
  };

  const resetSearch = () => {
    setSearchTerm('');
    setSearchParams({ page: '0' });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm) {
      const success = await deleteBooking(deleteConfirm.uhid_no);
      if (success) setDeleteConfirm(null);
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const paginatedBookings = filteredRows.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <div className="w-full max-w-7xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">
      <BookingFilters 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        handleDateFilter={handleDateFilterAction}
        handleClearFilters={handleClearFilters}
        dateError={dateError}
        onPageReset={() => setSearchParams({ page: '0' })}
        resetSearch={resetSearch}
      />

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={() => navigate('/addOtBooking')}
              className="flex items-center justify-center gap-2 bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors font-medium w-full sm:w-auto"
            >
              <Plus className="h-4 w-4" />
              Add OT Booking
            </button>

            <button
              onClick={() => handleExportPDF(filteredRows)}
              className="flex items-center justify-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium w-full sm:w-auto"
            >
              <Download className="h-4 w-4" />
              Export PDF
            </button>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Results Summary */}
      {(searchTerm || startDate || endDate) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            Found {filteredRows.length} booking{filteredRows.length !== 1 ? 's' : ''} 
            {searchTerm && ` matching "${searchTerm}"`}
            {startDate && endDate && ` between ${startDate} and ${endDate}`}
          </p>
        </div>
      )}

      {/* Views */}
      <BookingTable 
        bookings={paginatedBookings}
        onEdit={(uhid) => navigate(`/editOtBooking/${uhid}`, {
          state: { activeMenu: 'OT Booking', page: currentPage }
        })}
        onDelete={(uhid) => setDeleteConfirm(otBookings.find(b => b.uhid_no === uhid))}
        onClearFilters={handleClearFilters}
        searchTerm={searchTerm}
        startDate={startDate}
        endDate={endDate}
        sortConfig={sortConfig}
        requestSort={requestSort}
      />

      <BookingMobileCards 
        bookings={paginatedBookings}
        onEdit={(uhid) => navigate(`/editOtBooking/${uhid}`, {
          state: { activeMenu: 'OT Booking', page: currentPage }
        })}
        onDelete={(uhid) => setDeleteConfirm(otBookings.find(b => b.uhid_no === uhid))}
        onClearFilters={handleClearFilters}
        searchTerm={searchTerm}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Pagination */}
      {filteredRows.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredRows.length)} of {filteredRows.length} bookings
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchParams({ page: '0' })}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold uppercase tracking-widest"
              >
                First
              </button>
              <button
                onClick={() => setSearchParams({ page: (currentPage - 1).toString() })}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
                {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setSearchParams({ page: (currentPage + 1).toString() })}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSearchParams({ page: (totalPages - 1).toString() })}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold uppercase tracking-widest"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal 
        isOpen={!!deleteConfirm}
        booking={deleteConfirm}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
};

export default OTBookingList;
